import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { Overline } from "@/components/ui-bits";
import { toast } from "sonner";
import { Users, Plus, Copy, Trash2, ShieldCheck, User, Link2, CheckCircle2, Clock } from "lucide-react";

export default function Team() {
  const { user } = useOutletContext();
  const isAdmin = user?.role === "admin";
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [creating, setCreating] = useState(false);

  const load = () => {
    api.get("/team").then((r) => setMembers(r.data)).catch(() => {});
    if (isAdmin) api.get("/invites").then((r) => setInvites(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const createInvite = async () => {
    setCreating(true);
    try {
      await api.post("/invites", { role: "operator" });
      toast.success("Invite code generated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  const revoke = async (id) => {
    try { await api.delete(`/invites/${id}`); toast.success("Invite revoked"); load(); }
    catch { toast.error("Failed"); }
  };

  const copyLink = (code) => {
    const link = `${window.location.origin}/join?code=${code}`;
    navigator.clipboard.writeText(link);
    toast.success("Invite link copied", { description: link });
  };

  return (
    <div className="space-y-6" data-testid="team-page">
      <div>
        <Overline>Workspace Access & Roles</Overline>
        <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">Team Seats</h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Members */}
        <div className="hud-card rounded-lg p-5">
          <div className="flex items-center justify-between">
            <Overline>Members</Overline>
            <Users className="w-4 h-4 text-cyan-400" />
          </div>
          <div className="mt-4 space-y-2" data-testid="members-list">
            {members.map((m) => (
              <div key={m.id} className="flex items-center justify-between rounded-md border border-slate-800 bg-[#080B11] px-3 py-2.5">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center border ${m.role === "admin" ? "bg-cyan-500/10 border-cyan-500/30 text-cyan-400" : "bg-slate-800 border-slate-700 text-slate-400"}`}>
                    {m.role === "admin" ? <ShieldCheck className="w-4 h-4" /> : <User className="w-4 h-4" />}
                  </div>
                  <div>
                    <div className="text-sm font-medium">{m.name}</div>
                    <div className="text-[11px] font-mono text-slate-500">{m.email}</div>
                  </div>
                </div>
                <span className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${m.role === "admin" ? "text-cyan-400 border-cyan-500/30 bg-cyan-500/10" : "text-slate-400 border-slate-700 bg-slate-800/50"}`}>{m.role}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Invites */}
        <div className="hud-card accent-top rounded-lg p-5" style={{ "--accent-line": "#06B6D4" }}>
          <div className="flex items-center justify-between">
            <Overline>Invite Codes</Overline>
            {isAdmin && (
              <button data-testid="create-invite-btn" onClick={createInvite} disabled={creating}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-cyan-500 text-[#080B11] font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-cyan-400 transition-colors disabled:opacity-60">
                <Plus className="w-3 h-3" /> New Invite
              </button>
            )}
          </div>
          {!isAdmin && <p className="mt-4 text-xs text-slate-500">Only admins can manage invites.</p>}
          <div className="mt-4 space-y-2" data-testid="invites-list">
            {isAdmin && invites.length === 0 && <div className="text-xs text-slate-600 font-mono py-6 text-center">No invites yet. Generate one to add an operator.</div>}
            {invites.map((inv) => (
              <div key={inv.id} className="rounded-md border border-slate-800 bg-[#080B11] px-3 py-2.5" data-testid={`invite-${inv.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono font-bold text-cyan-400 tracking-widest">{inv.code}</span>
                  <div className="flex items-center gap-2">
                    {inv.used_by
                      ? <span className="flex items-center gap-1 text-[10px] font-mono text-emerald-400"><CheckCircle2 className="w-3 h-3" /> used</span>
                      : <span className="flex items-center gap-1 text-[10px] font-mono text-amber-400"><Clock className="w-3 h-3" /> pending</span>}
                    <span className="font-mono text-[10px] text-slate-500 uppercase">{inv.role}</span>
                  </div>
                </div>
                <div className="mt-2 flex gap-2">
                  <button data-testid={`copy-invite-${inv.id}`} onClick={() => copyLink(inv.code)} className="flex items-center gap-1.5 px-2.5 py-1 rounded border border-slate-700 text-slate-300 font-mono text-[10px] uppercase hover:bg-slate-800 transition-colors">
                    <Link2 className="w-3 h-3" /> Copy Link
                  </button>
                  <button data-testid={`revoke-invite-${inv.id}`} onClick={() => revoke(inv.id)} className="flex items-center gap-1.5 px-2.5 py-1 rounded border border-red-500/30 text-red-400 font-mono text-[10px] uppercase hover:bg-red-500/15 transition-colors">
                    <Trash2 className="w-3 h-3" /> Revoke
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
